import { useEffect, useRef } from "react";

export default function OracleOrb({ phase, onEnter, images, size = 320 }) {
  const canvasRef = useRef(null);
  const stRef     = useRef({ nodes:[], mouse:{ x:-9999, y:-9999 } });
  const animRef   = useRef(null);
  const phaseRef  = useRef(phase);

  useEffect(() => { phaseRef.current = phase; }, [phase]);

  useEffect(() => {
    const S     = size;
    const CX    = S / 2;
    const CY    = S / 2;
    const ORB_R = S / 2 - 10;

    const sourceImgs = images?.length > 0
      ? [...images].sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0))
      : [];
    const orbImgs    = sourceImgs.slice(0, 16);
    const isEmpty    = orbImgs.length === 0;

    // If empty, generate ghost nodes (no images) to show animated neural-net sphere
    const nodes = isEmpty ? Array.from({ length: 10 }, (_, i) => {
      const angle = (i / 10) * Math.PI * 2;
      return {
        id: i, imgEl: null, loaded: false, ghost: true,
        x: CX + Math.cos(angle) * ORB_R * .38 + (Math.random() - .5) * 14,
        y: CY + Math.sin(angle) * ORB_R * .38 + (Math.random() - .5) * 14,
        vx: (Math.random() - .5) * .5,
        vy: (Math.random() - .5) * .5,
        nr: 22,
      };
    }) : orbImgs.map((img, i) => {
      const angle  = (i / orbImgs.length) * Math.PI * 2;
      const el     = new Image();
      const baseNr = Math.max(16, Math.floor(S / 9) - Math.floor(i * 1.2));
      const nr     = img.saved
        ? Math.min(baseNr + 14, 54)
        : i < 3 ? baseNr + 8 : i < 7 ? baseNr + 3 : Math.max(14, baseNr - 4);
      const node = {
        id: img.id, imgEl: el, loaded: false,
        x: CX + Math.cos(angle) * ORB_R * .38 + (Math.random() - .5) * 14,
        y: CY + Math.sin(angle) * ORB_R * .38 + (Math.random() - .5) * 14,
        vx: (Math.random() - .5) * .5,
        vy: (Math.random() - .5) * .5,
        nr,
      };

      // Try with CORS first, fall back without if blocked
      el.crossOrigin = "anonymous";
      el.onload = () => { node.loaded = true; };
      el.onerror = () => {
        // Retry without crossOrigin (works for display, taints canvas  -  fine for orb)
        const fallback = new Image();
        fallback.onload = () => { node.imgEl = fallback; node.loaded = true; };
        fallback.onerror = () => {}; // give up, show placeholder
        fallback.src = img.url + (img.url.includes("?") ? "&" : "?") + "_cb=" + Date.now();
        node.imgEl = fallback;
      };
      el.src = img.url;
      return node;
    });
    stRef.current.nodes = nodes;

    function physics(nodes, mouse) {
      nodes.forEach(n => {
        n.vx += (CX - n.x) * .003; n.vy += (CY - n.y) * .003;
        const mdx = n.x - mouse.x, mdy = n.y - mouse.y, md = Math.hypot(mdx, mdy);
        if (md < 100 && md > .5) { const f = ((100 - md) / 100) * 3; n.vx += (mdx / md) * f; n.vy += (mdy / md) * f; }
        nodes.forEach(o => {
          if (o.id === n.id) return;
          const ox = n.x - o.x, oy = n.y - o.y, od = Math.hypot(ox, oy), minD = n.nr + o.nr + 6;
          if (od < minD && od > .5) { const f = ((minD - od) / minD) * .7; n.vx += (ox / od) * f; n.vy += (oy / od) * f; }
        });
        n.vx *= .91; n.vy *= .91; n.x += n.vx; n.y += n.vy;
        const dist = Math.hypot(n.x - CX, n.y - CY), maxR = ORB_R - n.nr - 4;
        if (dist > maxR && dist > .5) {
          const ang = Math.atan2(n.y - CY, n.x - CX);
          n.x = CX + Math.cos(ang) * maxR; n.y = CY + Math.sin(ang) * maxR;
          const nx = Math.cos(ang), ny = Math.sin(ang), dot = n.vx * nx + n.vy * ny;
          n.vx -= 2 * dot * nx * .4; n.vy -= 2 * dot * ny * .4;
        }
      });
    }

    function draw(canvas, nodes) {
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, S, S);
      ctx.save(); ctx.beginPath(); ctx.arc(CX, CY, ORB_R, 0, Math.PI * 2); ctx.clip();

      // Background
      const grd = ctx.createRadialGradient(CX - 30, CY - 40, 10, CX, CY, ORB_R);
      grd.addColorStop(0, "rgba(255,255,255,.07)"); grd.addColorStop(1, "rgba(5,5,14,1)");
      ctx.fillStyle = grd; ctx.fillRect(0, 0, S, S);

      // ── Neural network lines ──────────────────────────────────────────
      // Each node connects to its 2-3 nearest neighbours
      nodes.forEach((n, i) => {
        const distances = nodes
          .map((o, j) => ({ j, d: Math.hypot(n.x - o.x, n.y - o.y) }))
          .filter(({ j }) => j !== i)
          .sort((a, b) => a.d - b.d)
          .slice(0, 3); // connect to 3 nearest

        distances.forEach(({ j, d }) => {
          if (j < i) return;
          const o = nodes[j];
          const maxDist = ORB_R * 1.1;
          if (d > maxDist) return;
          const alpha = (1 - d / maxDist) * 0.55;
          ctx.beginPath();
          ctx.moveTo(n.x, n.y);
          ctx.lineTo(o.x, o.y);
          ctx.strokeStyle = `rgba(180,200,255,${alpha})`;
          ctx.lineWidth = 0.8;
          ctx.stroke();
        });
      });

      // ── Image nodes ───────────────────────────────────────────────────
      nodes.forEach(n => {
        if (!n.loaded) {
          ctx.beginPath(); ctx.arc(n.x, n.y, n.nr, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(255,255,255,.06)"; ctx.fill();
          // Small dot for unloaded
          ctx.beginPath(); ctx.arc(n.x, n.y, 2, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(200,210,255,.3)"; ctx.fill();
          return;
        }
        ctx.save(); ctx.beginPath(); ctx.arc(n.x, n.y, n.nr, 0, Math.PI * 2); ctx.clip();
        // object-fit:cover  -  crop to fill circle without stretching
        {
          const iw = n.imgEl.naturalWidth || n.imgEl.width || 1;
          const ih = n.imgEl.naturalHeight || n.imgEl.height || 1;
          const d  = n.nr * 2; // destination size (square bounding the circle)
          const scale = Math.max(d / iw, d / ih);
          const sw = d / scale, sh = d / scale;
          const sx = (iw - sw) / 2, sy = (ih - sh) / 2;
          ctx.drawImage(n.imgEl, sx, sy, sw, sh, n.x - n.nr, n.y - n.nr, d, d);
        }
        // Subtle edge ring
        ctx.strokeStyle = n.saved ? "rgba(232,255,71,.5)" : "rgba(255,255,255,.1)";
        ctx.lineWidth = n.saved ? 1.5 : 0.5;
        ctx.stroke();
        ctx.restore();
      });

      ctx.restore();
    }

    let running = true;
    function loop() { if (!running) return; physics(stRef.current.nodes, stRef.current.mouse); draw(canvasRef.current, stRef.current.nodes); animRef.current = requestAnimationFrame(loop); }
    animRef.current = requestAnimationFrame(loop);
    return () => { running = false; if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [images, size]);

  const handleMouse = e => {
    if (!canvasRef.current) return;
    const r = canvasRef.current.getBoundingClientRect();
    stRef.current.mouse = { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  return (
    <div
      style={{ width:`${size}px`, height:`${size}px`, borderRadius:"50%", animation:phase==="bursting"?"burst-out .75s ease forwards":"orb-breathe 3s ease-in-out infinite", cursor:"pointer" }}
      onMouseMove={handleMouse}
      onMouseLeave={() => { stRef.current.mouse = { x:-9999, y:-9999 }; }}
      onClick={onEnter}
      onTouchEnd={e => { e.preventDefault(); onEnter?.(); }}
      onTouchMove={e => {
        const t = e.touches[0];
        if (!t || !canvasRef.current) return;
        const r = canvasRef.current.getBoundingClientRect();
        stRef.current.mouse = { x: t.clientX - r.left, y: t.clientY - r.top };
      }}
    >
      <canvas ref={canvasRef} width={size} height={size} style={{ borderRadius:"50%", display:"block" }}/>
    </div>
  );
}
